import { useMemo } from "react";
import { HeaderBar } from "./HeaderBar";
import { StagePanel } from "./StagePanel";
import { ShowdownView } from "./ShowdownView";
import { HandNavFooter } from "./HandNavFooter";
import { MultiBoardsView } from "./modules/MultiBoardsView";
import { InsuranceEventsView } from "./modules/InsuranceEventsView";
import { ChopPoolsView } from "./modules/ChopPoolsView";
import { HuntersView } from "./modules/HuntersView";
import { SquidGameView } from "./modules/SquidGameView";
import { stageLabel, fmtChips, inferChipFactor, displayChips } from "./helpers";

/**
 * NativeHandDetails — vista pixel-perfect del HistoryDetailPanel nativo PPPoker.
 *
 * Estructura nativa (top → bottom):
 *  - HeaderBar (date + ◎ active/total + hand_id)
 *  - StagePanel × 4 (Preflop con dorsos, Flop, Turn, River)
 *  - StagePanel "Showdown" (label sin acciones, solo pot total)
 *  - ShowdownView (cada jugador con hole cards + result + profit + board)
 *  - Modules (multi-board, insurance, chops, hunters, squid)
 *  - HandNavFooter (heart + ◀ N/total ▶ + ⌃)
 */
export function NativeHandDetails({
  data,
  handIndex,
  totalHands,
  collectedCount,
  collectedTotal = 100,
  onPrevHand,
  onNextHand,
  onCollapse,
  onToggleHeart,
  matchedUidSet,
}) {
  const playerByUid = useMemo(() => {
    const map = {};
    (data?.players || []).forEach((p) => {
      if (p.uid != null) map[p.uid] = p;
    });
    return map;
  }, [data?.players]);

  // Chip factor: infer from BB action; used to convert raw → visual chips.
  const chipFactor = useMemo(() => inferChipFactor(data), [data]);

  // Group actions by stage so StagePanel can render them inline
  const rounds = useMemo(() => {
    const baseRounds = data?.rounds || [];
    const allActions = data?.actions || [];
    if (!baseRounds.length) return [];
    const byStage = {};
    allActions.forEach((a) => {
      const k = a.stage ?? a.stage_id;
      if (k == null) return;
      (byStage[k] = byStage[k] || []).push(a);
    });
    Object.values(byStage).forEach((arr) =>
      arr.sort((a, b) => (a.seq ?? a.action_index ?? 0) - (b.seq ?? b.action_index ?? 0)),
    );
    const cumulativeBoard = [];
    const cumulativeBoard2 = [];
    return baseRounds.map((r, i) => {
      const explicitBoard = r.board_labels || r.board || [];
      const addedBoard = r.board_added_labels || r.board_added || [];
      const explicitBoard2 = r.board_2_labels || r.board_2 || [];
      const addedBoard2 = r.board_2_added_labels || r.board_2_added || [];

      const boardForStage = explicitBoard.length
        ? (() => {
            cumulativeBoard.splice(0, cumulativeBoard.length, ...explicitBoard);
            return [...cumulativeBoard];
          })()
        : (() => {
            if (addedBoard.length) cumulativeBoard.push(...addedBoard);
            return [...cumulativeBoard];
          })();
      const board2ForStage = explicitBoard2.length
        ? (() => {
            cumulativeBoard2.splice(0, cumulativeBoard2.length, ...explicitBoard2);
            return [...cumulativeBoard2];
          })()
        : (() => {
            if (addedBoard2.length) cumulativeBoard2.push(...addedBoard2);
            return [...cumulativeBoard2];
          })();

      // ⭐ Pot que se muestra como "label" de la calle.
      // PPPoker nativo muestra el pot AL INICIO de cada calle, no al final.
      // Preflop: caso especial → su propio pot_end (lo que se acumuló con blinds+antes+calls).
      // Flop/Turn/River: pot_end de la calle anterior (= lo que entra a esta calle).
      const stage = r.stage ?? r.stage_id;
      const isPreflop = stage === 1 || r.street === "preflop";
      const prev = baseRounds[i - 1];
      const potLabelChips = isPreflop
        ? (r.pot_end_chips ?? r.pot_chips ?? r.pot_total_chips)
        : (prev?.pot_end_chips ?? prev?.pot_chips ?? r.pot_end_chips);

      // ⭐ amount_in_street_chips: total acumulado de cada jugador en esa calle.
      // PPPoker nativo muestra "to amount" (street total) en lugar del "delta" individual.
      // Ej: SB 0.6 → CALL 4.8 (delta) ⇒ PPPoker dice 5.4 (total).
      const rawActions = r.actions && r.actions.length ? r.actions : byStage[stage] || [];
      const streetTotals = {};
      const enrichedActions = rawActions.map((a) => {
        const uid = a.uid;
        const amt = Number(a.amount_chips ?? a.amount ?? 0);
        if (uid && amt > 0) {
          streetTotals[uid] = (streetTotals[uid] ?? 0) + amt;
        }
        return {
          ...a,
          amount_in_street_chips: uid && streetTotals[uid] ? streetTotals[uid] : amt,
        };
      });

      return {
        ...r,
        board_display_labels: boardForStage,
        board_2_display_labels: board2ForStage,
        actions: enrichedActions,
        pot_label_chips: potLabelChips,
      };
    });
  }, [data?.rounds, data?.actions]);

  if (!data) {
    return (
      <div className="py-8 text-center text-sm text-slate-500" data-testid="native-detail-empty">
        Sin datos
      </div>
    );
  }

  const summary = data.summary || {};
  const hand = data.hand || {};
  const players = data.players || [];

  // Active = jugadores que llegaron al showdown (mostraron o ganaron).
  // Si nadie llegó (todos foldearon excepto winner), usamos el total como aprox.
  const activePlayers =
    players.filter((p) => p.is_winner || p.won_hand || p.went_to_showdown || p.showed_cards).length ||
    players.length ||
    summary.num_players;
  const totalPlayers = summary.num_players ?? players.length;

  // Final pot
  const finalPotChips = summary.pot_size_chips ?? summary.pot_size ?? rounds[rounds.length - 1]?.pot_end_chips;
  const finalBoard = summary.board || [];
  const finalBoard2 = summary.board_2 || hand.board_2 || [];

  const insuranceEvents =
    data.insurance_events ||
    rounds.flatMap((r) => (r.insurance_events || []).map((ev) => ({ ...ev, stage: r.stage })));
  const chops =
    data.chops ||
    rounds.flatMap((r) =>
      (r.chop_pools || []).flatMap((p) =>
        (p.users || []).map((u) => ({ ...u, stage: r.stage, pool_id: p.pool_id })),
      ),
    );
  const hunters = data.hunters || [];
  const squidWinner = hand.squid_winner || hand.squid_winner_uids || [];
  const squidLoser = hand.squid_loser || hand.squid_loser_uids || [];

  const finalStage = summary.final_stage;
  const isDoubleBoard = !!(hand.is_double_board ?? summary.is_double_board);
  // ⭐ En double board, el final_stage suele quedar en 4 aunque haya river+showdown.
  // Si hay 5 cartas en el board (river completo), consideramos que llegó al showdown.
  const board1 = summary.board || hand.board_labels || [];
  const board2 = summary.board_2 || hand.board_2_labels || [];
  const reachedShowdown =
    finalStage === 5 || finalStage === "SHOWDOWN" || finalStage === "showdown" ||
    players.some((p) => p.went_to_showdown || p.showed_cards) ||
    (board1.length >= 5 && players.some((p) => (p.hole_cards || []).length > 0));

  // Pot pools del último round (Main + Side1 + Side2...)
  const lastRound = rounds[rounds.length - 1];
  const potPools = lastRound?.pot_pools_chips || hand.pot_pools_chips || [];

  return (
    <div
      className="rounded-lg border border-[#303545] bg-[#0d1019] overflow-hidden"
      data-testid="native-hand-details"
    >
      <HeaderBar
        summary={summary}
        hand={hand}
        activePlayers={activePlayers}
        totalPlayers={totalPlayers}
      />

      <div>
        {rounds.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">Sin rondas</div>
        ) : (
          rounds.map((r, idx) => (
            <StagePanel
              key={`${r.stage}-${idx}`}
              round={r}
              playerByUid={playerByUid}
              chipFactor={chipFactor}
              isPreflop={idx === 0 && (r.stage === 1 || stageLabel(r.stage, r.street) === "Preflop")}
              matchedUidSet={matchedUidSet}
            />
          ))
        )}

        {/* Pozo Main + Side (cuando hay split pots de all-ins parciales) */}
        {potPools.length > 1 && (
          <section
            className="border-b border-white/5 bg-[#1b1f2b] px-3 py-2"
            data-testid="pot-pools-row"
          >
            <div className="grid items-center gap-2" style={{ gridTemplateColumns: "130px minmax(0, 1fr) auto" }}>
              <span className="text-[15px] font-medium text-slate-300">Pozo</span>
              <div className="text-right text-[14px] tabular-nums text-slate-100">
                {potPools.map((pool, i) => {
                  const v = displayChips(pool, chipFactor);
                  const label = i === 0 ? "Main" : `Side ${i}`;
                  return (
                    <span key={i} className="ml-3" data-testid={`pot-pool-${i}`}>
                      <span className="text-slate-400 mr-1">{label}</span>
                      <span className="font-bold">({fmtChips(v)})</span>
                    </span>
                  );
                })}
              </div>
              <span />
            </div>
          </section>
        )}

        {/* Showdown stage row (label + final pot + side desglose) */}
        {reachedShowdown && (
          <section
            className="border-b border-white/5 bg-[#1b1f2b] px-3 py-3"
            data-testid="stage-panel-showdown"
          >
            <div className="grid items-center gap-2" style={{ gridTemplateColumns: "130px minmax(0, 1fr) 84px" }}>
              <span className="text-[22px] font-extrabold text-[#f6f6f7]">Showdown</span>
              <div />
              <span className="text-[20px] font-extrabold tabular-nums text-[#f4f4f7] text-right">
                {fmtChips(displayChips(finalPotChips, chipFactor))}
              </span>
            </div>
            {/* Pozo Main+Side (segunda fila debajo de Showdown — espejo PPPoker) */}
            {potPools.length > 1 && (
              <div className="mt-1 grid items-center gap-2" style={{ gridTemplateColumns: "130px minmax(0, 1fr)" }}>
                <span className="text-[14px] text-slate-400">Pozo</span>
                <div className="text-right text-[13px] tabular-nums text-slate-100" data-testid="pot-pools-showdown">
                  {potPools.map((pool, i) => (
                    <span key={i} className="ml-3">
                      <span className="text-slate-400 mr-1">{i === 0 ? "Main" : `Side ${i}`}</span>
                      <span className="font-bold">({fmtChips(displayChips(pool, chipFactor))})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Showdown player cards section */}
      {reachedShowdown && players.length > 0 && (
        <ShowdownView
          players={players}
          board={finalBoard.length ? finalBoard : board1}
          board2={finalBoard2.length ? finalBoard2 : board2}
          chipFactor={chipFactor}
          isDoubleBoard={isDoubleBoard}
          matchedUidSet={matchedUidSet}
        />
      )}

      {/* Resultado sin showdown: cuando todos foldearon excepto el ganador,
          PPPoker nativo igual muestra los profits por jugador. Espejamos eso. */}
      {!reachedShowdown && players.some((p) => p.is_winner) && (
        <FoldedResult players={players} chipFactor={chipFactor} matchedUidSet={matchedUidSet} />
      )}

      {/* Future-proof modules */}
      <MultiBoardsView
        multiBoardsLabels={hand.multi_boards_labels}
        multiBoardsRaw={hand.multi_boards_raw}
      />
      <InsuranceEventsView events={insuranceEvents} playerByUid={playerByUid} />
      <ChopPoolsView chops={chops} playerByUid={playerByUid} />
      <HuntersView hunters={hunters} />
      <SquidGameView winners={squidWinner} losers={squidLoser} />

      {/* Footer navigation */}
      {(handIndex != null || onPrevHand || onNextHand) && (
        <HandNavFooter
          collectedCount={collectedCount}
          collectedTotal={collectedTotal}
          currentIndex={handIndex}
          totalHands={totalHands}
          onPrev={onPrevHand}
          onNext={onNextHand}
          onCollapse={onCollapse}
          onToggleHeart={onToggleHeart}
        />
      )}
    </div>
  );
}

export default NativeHandDetails;

// ---------------------------------------------------------------------------
// FoldedResult: panel de "Resultado" cuando NO hubo showdown.
// PPPoker nativo igual muestra los profits por jugador (verde el que ganó por
// fold de todos los demás, rojo los que perdieron lo apostado). Lo espejamos.
//
// Nota: usa `profit_total_chips` directamente del backend (NO chip_factor),
// porque ya viene normalizado en visual desde el persister.
// ---------------------------------------------------------------------------
function FoldedResult({ players, chipFactor, matchedUidSet }) {
  // Ordenamos: ganador primero (verde), luego por profit descendente.
  const sorted = [...players].sort((a, b) => {
    if (a.is_winner !== b.is_winner) return a.is_winner ? -1 : 1;
    return (b.profit_visual ?? b.profit_total_chips ?? 0) -
           (a.profit_visual ?? a.profit_total_chips ?? 0);
  });

  return (
    <section
      className="border-t border-white/5 bg-[#1b1f2b] px-3 py-3"
      data-testid="folded-result-panel"
    >
      <div className="grid items-center gap-2 mb-2" style={{ gridTemplateColumns: "130px minmax(0, 1fr)" }}>
        <span className="text-[18px] font-extrabold text-[#f6f6f7]">Resultado</span>
        <span className="text-right text-[12px] text-slate-400">
          Ganador por fold (sin showdown)
        </span>
      </div>
      <div className="space-y-1">
        {sorted.map((p) => {
          const profit = p.profit_visual ?? p.profit_total_chips ?? 0;
          const isWin = !!p.is_winner;
          const positive = profit > 0;
          const negative = profit < 0;
          const profitColor = positive
            ? "text-emerald-400"
            : negative
            ? "text-rose-400"
            : "text-slate-300";
          return (
            <div
              key={p.hand_player_id || p.uid}
              className="grid items-center gap-2 px-2 py-1.5 rounded"
              style={{ gridTemplateColumns: "60px minmax(0, 1fr) auto", background: isWin ? "rgba(16, 185, 129, 0.08)" : "transparent" }}
              data-testid="folded-result-row"
            >
              <span className="text-[11px] text-slate-400 font-medium">
                {p.position || `S${p.seat ?? "?"}`}
              </span>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[14px] text-slate-100 truncate font-medium">
                  {p.nick || `uid_${p.uid}`}
                </span>
                {matchedUidSet?.has(Number(p.uid)) && (
                  <span
                    className="text-amber-400 text-[12px] leading-none"
                    title="UID en MATCH (collusion_suspected)"
                    data-testid={`match-stars-folded-${p.uid}`}
                  >⭐⭐</span>
                )}
                {isWin && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white"
                    data-testid="folded-result-win-badge"
                  >
                    WIN
                  </span>
                )}
              </div>
              <span className={`text-[14px] tabular-nums font-bold ${profitColor}`}>
                {positive ? "+" : ""}{fmtChips(profit)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
